import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Radio, Select, Space, Switch, Table, Typography } from 'antd';
import _ from 'lodash';
import { filterOption, orderedProducts, productChangePaymentOptions } from 'modules/utilities/utility';
import { useMonthKitData, useProductMapping } from 'modules/dashboards/actionables_v2/store';
import { CATEGORY, categories } from 'modules/my_tickets/data';
import { postProductChangeQuote } from './api';
import ProductCalculation from './ProductCalculation';

const { Text } = Typography;

const DEFAULT_PAYMENT_OPTION = productChangePaymentOptions[0].value;

// product_principal_id IS the Shopify variant id the backend keys on: getMedicineDataCached builds
// its map as medicineMap[product_principal_id] = { variantId: product_principal_id, ... }. So the id
// sent to /product-change is unchanged by switching to the curated list — only the list narrows.
const toVariantOptions = (products) => {
    // orderedProducts can return the same product twice — customOrder lists a few ids twice, and the
    // Recap / Active Serum / Defence Conditioner branches re-push a product found earlier — and the
    // odd undefined from a find() that matched nothing. Keying by variant id collapses all of that.
    const byVariantId = new Map();

    (products || []).forEach((product) => {
        const variantId = _.get(product, ['product_principal_id'], null);
        const displayName = _.get(product, ['medicine', 'display_name'], null);
        if (!variantId || !displayName || byVariantId.has(String(variantId))) return;
        byVariantId.set(String(variantId), { value: String(variantId), label: displayName });
    });

    return [...byVariantId.values()];
};

const Field = ({ label, children, plain }) => (
    <Space.Compact block className='my-3'>
        <span
            style={{ background: '#FAFAFA', border: '#D9D9D9', width: '30%' }}
            className='border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2'
        >
            <Text>{label}</Text>
        </span>
        {plain ? (
            <div
                style={{ width: '70%', background: '#FAFAFA', border: '1px solid #D9D9D9' }}
                className='border-start-0 d-flex align-items-center px-3 py-1 gap-2'
            >
                {children}
            </div>
        ) : children}
    </Space.Compact>
);

const isCodOrder = (order) => {
    const meta = _.get(order, ['order_meta'], {});
    return `${_.get(meta, 'tags', '')} ${_.get(meta, 'gateway', '')}`.toLowerCase().includes('cod');
};

const findSubcategory = (subcategoryOptions, value) => (subcategoryOptions || []).find(option => option.value === value);
const assigneeOf = (subcategory) => _.get(subcategory, ['assignees', 0, 'userId'], null);

const resolveReturnSubcategory = (order, subcategoryOptions) => {
    const value = isCodOrder(order) ? CATEGORY.RETURN_FOR_COD_ORDERS : CATEGORY.RETURN_FOR_PREPAID_ORDER;
    const subcategory = findSubcategory(subcategoryOptions, value);
    const assignee = assigneeOf(subcategory);

    if (!subcategory) return { value, error: `${categories[value] || value} is not available under this category, so the return ticket cannot be raised` };
    if (!assignee) return { value, error: `${categories[value] || value} has no assignee configured, so the return ticket cannot be raised` };

    return { value, sub_category: value, sub_category_id: subcategory._id, current_assignment: assignee };
};

const ProductChange = ({ order, subcategoryOptions, shipmentAddress, setShipmentAddress, setTicketPayload, setValidation, customerData }) => {
    const { productsData, fetchProducts } = useProductMapping();
    const { kitData, fetchMonthKitData } = useMonthKitData();
    const [replacementRequired, setReplacementRequired] = useState(false);
    const [replacementVariantIds, setReplacementVariantIds] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [paymentOption, setPaymentOption] = useState(DEFAULT_PAYMENT_OPTION);
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState(null);
    const returnSubcategory = resolveReturnSubcategory(order, subcategoryOptions);

    const caseId = _.get(customerData, ['id'], null);

    // Same two sources the place-order panel reads, so an agent is offered one catalogue rather than
    // two. fetchProducts self-guards against a refetch and persists its result, so calling it here is
    // cheap; kitData is what lets orderedProducts add the formulation, Recap and Kamdudha extras.
    useEffect(() => {
        if (_.isEmpty(productsData)) fetchProducts();
    }, [productsData, fetchProducts]);

    useEffect(() => {
        if (caseId) fetchMonthKitData(caseId);
    }, [caseId, fetchMonthKitData]);

    // orderedProducts reads customerData.caseTags without guarding customerData itself, and returns []
    // until productsData has loaded — so an empty selector is the honest state while either is missing.
    const products = useMemo(
        () => toVariantOptions(orderedProducts(customerData || {}, productsData || [], kitData)),
        [customerData, productsData, kitData]
    );

    useEffect(() => {
        setTicketPayload(prev => ({ ...prev, reverse_pickup_required: false, payment_option: DEFAULT_PAYMENT_OPTION }));

        return () => {
            setTicketPayload(({ reverse_pickup_required, payment_option, replacement_variant_ids, return_ticket, ...rest }) => rest);
            setValidation(({ replacement_product, products, request_type, return_ticket, ...rest }) => rest);
        };
    }, []);


    useEffect(() => {
        if (!replacementRequired) {
            setTicketPayload(({ return_ticket, ...rest }) => rest);
            setValidation(({ return_ticket, ...rest }) => rest);
            return;
        }
        setTicketPayload(prev => ({ ...prev, return_ticket: returnSubcategory.error ? null : returnSubcategory }));
        setValidation(prev => ({ ...prev, return_ticket: !returnSubcategory.error }));
    }, [replacementRequired, returnSubcategory.value, returnSubcategory.error, returnSubcategory.current_assignment]);

    useEffect(() => {
        setValidation(prev => ({ ...prev, replacement_product: replacementVariantIds.length > 0 }));
        setTicketPayload(prev => ({ ...prev, replacement_products: replacementVariantIds.map(variantId => ({ variantId: String(variantId), quantity: quantities[variantId] || 1 })) }));
    }, [replacementRequired, replacementVariantIds, quantities]);

    useEffect(() => {
        if (!replacementVariantIds.length || !order?.id) {
            setQuote(null);
            setQuoteError(null);
            return;
        }
        let cancelled = false;
        postProductChangeQuote({
            identifierId: order.id,
            newProducts: selectedProducts,
        })
            .then(response => {
                if (cancelled) return;
                setQuote(response.data);
                setQuoteError(null);
            })
            .catch(error => {
                if (cancelled) return;
                setQuote(null);
                setQuoteError(_.get(error, ['response', 'data', 'message'], 'Could not price the replacement products'));
            });
        return () => { cancelled = true; };
    }, [replacementVariantIds, quantities, order?.id]);

    const onReplacementRequiredChange = (checked) => {
        setReplacementRequired(checked);
        setTicketPayload(prev => ({ ...prev, reverse_pickup_required: checked }));
        if (!checked) {
            // ProductCalculation leaves products:false in validation when it unmounts, which would
            // keep the create button disabled after the toggle goes back off. The replacement
            // selection is deliberately left alone — it is independent of the pickup.
            setValidation(({ products, request_type, ...rest }) => rest);
            setTicketPayload(({ products, total_refund_price, ...rest }) => rest);
        }
    };

    const selectedProducts = replacementVariantIds.map(variantId => ({ variantId: String(variantId), quantity: quantities[variantId] || 1 }));

    const onReplacementChange = (variantIds) => {
        setReplacementVariantIds(variantIds);
        setQuantities(prev => variantIds.reduce((acc, id) => ({ ...acc, [id]: prev[id] || 1 }), {}));
    };

    const onQuantityChange = (variantId, delta) => {
        setQuantities(prev => ({ ...prev, [variantId]: Math.max((prev[variantId] || 1) + delta, 1) }));
    };

    const onPaymentOptionChange = (event) => {
        setPaymentOption(event.target.value);
        setTicketPayload(prev => ({ ...prev, payment_option: event.target.value }));
    };

    return (
        <div>
            <Field label='Reverse Pickup Required' plain>
                <Switch checked={replacementRequired} onChange={onReplacementRequiredChange} />
                <Text type='secondary'>
                    {replacementRequired
                        ? 'Selected products will be picked up and refunded'
                        : 'Off — no pickup is raised, only the replacement order is placed'}
                </Text>
            </Field>

            {replacementRequired && (
                <>
                    <Field label='Return Ticket' plain>
                        {returnSubcategory.error
                            ? <Text type='danger'>{returnSubcategory.error}</Text>
                            : <Text type='secondary'>{categories[returnSubcategory.value] || returnSubcategory.value} — raised first, and it carries the refund</Text>}
                    </Field>

                    <ProductCalculation
                        order={order}
                        shipmentAddress={shipmentAddress}
                        setShipmentAddress={setShipmentAddress}
                        setTicketPayload={setTicketPayload}
                        setValidation={setValidation}
                    />
                </>
            )}

            <Field label='Replacement Product'>
                <Select
                    style={{ width: '70%' }}
                    showSearch
                    allowClear
                    mode='multiple'
                    size='middle'
                    placeholder='Search and select the products to be delivered'
                    filterOption={filterOption}
                    options={products}
                    value={replacementVariantIds}
                    onChange={onReplacementChange}
                />
            </Field>

            {quoteError && <Text type='danger' className='d-block mb-2'>{quoteError}</Text>}

            {quote && (
                <>
                    <Table
                        className='my-2'
                        pagination={false}
                        size='small'
                        rowKey='variant_id'
                        dataSource={quote.products}
                        columns={[
                            { title: 'Replacement Product', dataIndex: 'name', key: 'name' },
                            { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price' },
                            {
                                title: 'Qty',
                                key: 'quantity',
                                render: (_text, row) => (
                                    <Space.Compact>
                                        <Button disabled={(quantities[row.variant_id] || 1) <= 1} onClick={() => onQuantityChange(row.variant_id, -1)}>-</Button>
                                        <Input value={quantities[row.variant_id] || 1} style={{ width: '2.5rem' }} readOnly />
                                        <Button onClick={() => onQuantityChange(row.variant_id, 1)}>+</Button>
                                    </Space.Compact>
                                ),
                            },
                            { title: 'Amount', dataIndex: 'amount', key: 'amount' },
                        ]}
                    />
                    <div className='d-flex flex-column align-items-end mb-3 px-1'>
                        <Text type='secondary'>Total: ₹{quote.total_price}</Text>
                        <Text type='secondary'>Discount ({quote.discount_percentage}% from {quote.order_display_id}): -₹{quote.discount_amount}</Text>
                        <Text type='secondary'>Delivery: ₹{quote.delivery_charge}</Text>
                        <Text strong>Payable: ₹{quote.payable_amount}</Text>
                    </div>
                </>
            )}

            <Field label='Payment Option' plain>
                <Radio.Group value={paymentOption} onChange={onPaymentOptionChange} options={productChangePaymentOptions} />
            </Field>
        </div>
    );
};

export default ProductChange;
